import type { TaxEntity } from "../../domain/entities/TaxEntity";
import type { ChangeDetails } from "../../utils/reviewWorkflow";
import type { InvoiceBillLineLike } from "./invoiceBillDisplayValues";
import { buildInvoiceBillDisplayDiff } from "./buildInvoiceBillDisplayDiff";
import { BILL_HEADER_FIELDS, BILL_LINE_FIELDS } from "./invoiceBillAllowlists";
import { formatInvoiceTrackingFieldName } from "./invoicePendingReviewDisplayMaps";

function billHeaderFieldLabelCandidates(canonicalKey: string): string[] {
  const row = BILL_HEADER_FIELDS.find((f) => f.key === canonicalKey);
  const set = new Set<string>();
  if (row) set.add(row.label);
  set.add(formatInvoiceTrackingFieldName(canonicalKey));
  if (canonicalKey === "vendor_name") {
    set.add("Vendor");
  }
  return Array.from(set);
}

function resolveBillHeaderKeyFromChangeField(
  field: string,
): string | undefined {
  for (const { key } of BILL_HEADER_FIELDS) {
    if (billHeaderFieldLabelCandidates(key).includes(field)) {
      return key;
    }
  }
  return undefined;
}

function resolveBillLineKeyFromSubfieldLabel(
  subfieldLabel: string,
): string | undefined {
  for (const { key, label } of BILL_LINE_FIELDS) {
    if (subfieldLabel === label) return key;
    if (subfieldLabel === formatInvoiceTrackingFieldName(key)) return key;
  }
  return undefined;
}

const LINE_ITEM_FIELD_RE = /^Line Item (\d+): (.+)$/;
const LINE_ITEM_REMOVED_RE = /^Line Item (\d+)$/;

const SNAPSHOT_LINE_TOTAL_KEYS = new Set([
  "item_total",
  "item_total_inclusive_of_tax",
]);

export interface BillPendingReviewTextDiff {
  oldText: string;
  newText: string;
}

/** Whether a persisted `field_change.field` string is allowlisted for bill review display / tracking. */
export function isBillTrackedFieldChangeRowAllowed(field: string): boolean {
  if (!field) return false;
  if (field === "vendor_id") return false;
  if (field === "Vendor") return true;
  if (LINE_ITEM_REMOVED_RE.test(field)) return true;
  const lineMatch = LINE_ITEM_FIELD_RE.exec(field);
  if (lineMatch) {
    return resolveBillLineKeyFromSubfieldLabel(lineMatch[2]) !== undefined;
  }
  return resolveBillHeaderKeyFromChangeField(field) !== undefined;
}

/**
 * Strike-through maps for a **pending** bill review from `change_details.field_changes`
 * plus optional `original_json` vs `raw_json` snapshot for line totals only.
 */
export function buildBillPendingReviewDisplayMapsFromChangeDetails(
  changeDetails: ChangeDetails | null | undefined,
  taxes: TaxEntity[] | undefined,
): {
  header: Map<string, BillPendingReviewTextDiff>;
  line: Map<string, BillPendingReviewTextDiff>;
} {
  const header = new Map<string, BillPendingReviewTextDiff>();
  const line = new Map<string, BillPendingReviewTextDiff>();

  if (!changeDetails) {
    return { header, line };
  }

  const fieldChanges = changeDetails.field_changes;
  if (fieldChanges?.length) {
    for (const fc of fieldChanges) {
      const field = fc.field;
      if (!field || field === "vendor_id") continue;

      const lineMatch = LINE_ITEM_FIELD_RE.exec(field);
      if (lineMatch) {
        const lineNum = Number(lineMatch[1]);
        const sub = lineMatch[2];
        const lineKey = resolveBillLineKeyFromSubfieldLabel(sub);
        if (lineKey) {
          line.set(`${lineNum}-${lineKey}`, {
            oldText: fc.old,
            newText: fc.new,
          });
        }
        continue;
      }

      if (LINE_ITEM_REMOVED_RE.test(field)) {
        continue;
      }

      const headerKey = resolveBillHeaderKeyFromChangeField(field);
      if (headerKey) {
        header.set(headerKey, { oldText: fc.old, newText: fc.new });
      }
    }
  }

  if (changeDetails.original_json != null && changeDetails.raw_json != null) {
    const vs = changeDetails.validation_snapshot;
    const validationSnapshot =
      vs && Array.isArray(vs.line_items) && vs.line_items.length > 0
        ? {
            line_items: vs.line_items as
              | InvoiceBillLineLike[]
              | null
              | undefined,
          }
        : undefined;

    const items = buildInvoiceBillDisplayDiff({
      kind: "bill",
      originalHeader: changeDetails.original_json,
      newHeader: changeDetails.raw_json,
      originalLineItems: changeDetails.original_json?.line_items,
      newLineItems: changeDetails.raw_json?.line_items,
      taxes,
      validationSnapshot: validationSnapshot ?? null,
    });
    for (const d of items) {
      if (
        d.scope !== "line" ||
        d.lineNumber == null ||
        !SNAPSHOT_LINE_TOTAL_KEYS.has(d.fieldKey)
      ) {
        continue;
      }
      const k = `${d.lineNumber}-${d.fieldKey}`;
      if (!line.has(k)) {
        line.set(k, { oldText: d.oldText, newText: d.newText });
      }
    }
  }

  return { header, line };
}
